import { useEffect, useRef, useState } from "react";
import { api, errorText } from "@/lib/api";

const STATUS_STYLE = {
  queued: "var(--muted-text)", extracted: "var(--blue)", not_billing: "var(--muted-text)", no_recurring_found: "var(--muted-text)",
  needs_clearer_copy: "var(--amber)", unsupported: "var(--red)", failed: "var(--red)", cap_reached: "var(--amber)",
};
const STATUS_LABEL = {
  queued: "Checking…", extracted: "Extracted", not_billing: "Not a bill", no_recurring_found: "Nothing recurring", needs_clearer_copy: "Needs a clearer copy",
  unsupported: "Unsupported", failed: "Failed", cap_reached: "AI limit reached",
};

export const UploadPanel = ({ documents = [], onChange, cap, used }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(0);
  const [scope, setScope] = useState("business");
  const fileRef = useRef(null);
  const pending = documents.some((d) => d.status === "queued");

  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => onChange?.(), 2500);
    return () => clearInterval(t);
  }, [pending, onChange]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr(""); setProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("scope", scope);
    try {
      await api.post("/sources/upload", fd, { onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)) });
      await onChange?.();
    } catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="ts-card p-4" data-testid="upload-panel">
      <div className="flex items-center gap-2 mb-3 text-xs" data-testid="upload-scope">
        <span style={{ color: "var(--muted-text)" }}>Business or Personal?</span>
        {["business", "personal"].map((sc) => <button key={sc} className={`chip ${scope === sc ? "active" : ""}`} onClick={() => setScope(sc)} data-testid={`upload-scope-${sc}`}>{sc[0].toUpperCase() + sc.slice(1)}</button>)}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="ts-btn ts-btn-ghost cursor-pointer" htmlFor="file-input">{busy ? `Uploading ${progress}%` : "Choose a file"}</label>
        <input id="file-input" ref={fileRef} type="file" className="sr-only" accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.tsv,.xlsx,.txt,.md,.eml" onChange={(e) => upload(e.target.files?.[0])} disabled={busy} data-testid="file-input" />
        <span className="text-xs" style={{ color: "var(--muted-text)" }}>PDF, image, CSV, XLSX, TXT · under 15 MB{cap != null ? ` · ${used ?? documents.length}/${cap} used` : ""}</span>
      </div>
      {err && <p role="alert" className="ts-error mt-2" data-testid="upload-error">{err}</p>}
      {documents.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2 text-sm" data-testid="document-list">
          {documents.map((d) => (
            <li key={d.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t pt-2" style={{ borderColor: "var(--line-soft)" }} data-testid={`document-${d.id}`}>
              <span className="font-medium truncate max-w-[240px]" style={{ color: "var(--ink)" }}>{d.original_filename}</span>
              <span className="num text-xs font-semibold" style={{ color: STATUS_STYLE[d.status] || "var(--muted-text)" }}>{STATUS_LABEL[d.status] || d.status}</span>
              {d.items_found > 0 && <span className="num text-xs">{d.items_found} item{d.items_found !== 1 ? "s" : ""}</span>}
              <span className="text-xs w-full" style={{ color: "var(--muted-text)" }}>{d.status_note}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
